import { AlertTriangle, Download, FileText } from "lucide-react";
import { ReactNode } from "react";
import { PER_PAGE_OPTIONS, PageParams, Pagination } from "../../types/admin";

export const PageHeader = ({
  title,
  subtitle,
  action,
}: {
  title: string;
  subtitle?: string;
  action?: ReactNode;
}) => (
  <div className="flex items-start justify-between mb-6">
    <div>
      <h1 className="text-2xl font-bold text-gray-900">{title}</h1>
      {subtitle && <p className="text-sm text-gray-500 mt-1">{subtitle}</p>}
    </div>
    {action}
  </div>
);

export const Card = ({
  title,
  action,
  children,
  className = "",
}: {
  title?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) => (
  <div
    className={`bg-white rounded-lg shadow-sm border border-gray-100 ${className}`}
  >
    {(title || action) && (
      <div className="flex items-center justify-between px-5 py-3 border-b border-gray-100">
        {title && (
          <h2 className="text-sm font-semibold text-gray-700">{title}</h2>
        )}
        {action}
      </div>
    )}
    {children}
  </div>
);

export const StatCard = ({
  label,
  value,
  hint,
}: {
  label: string;
  value: ReactNode;
  hint?: string;
}) => (
  <div className="bg-white rounded-lg shadow-sm border border-gray-100 p-5">
    <p className="text-xs font-medium text-gray-500 uppercase tracking-wide">
      {label}
    </p>
    <p className="text-2xl font-bold text-gray-900 mt-2">{value}</p>
    {hint && <p className="text-xs text-gray-400 mt-1">{hint}</p>}
  </div>
);

const BADGE_COLORS: Record<string, string> = {
  gray: "bg-gray-100 text-gray-600",
  green: "bg-green-100 text-green-700",
  red: "bg-red-100 text-red-700",
  yellow: "bg-yellow-100 text-yellow-700",
  orange: "bg-orange-100 text-orange-700",
  blue: "bg-blue-100 text-blue-700",
  purple: "bg-purple-100 text-purple-700",
  indigo: "bg-indigo-100 text-indigo-700",
};

export const Badge = ({
  children,
  color = "gray",
}: {
  children: ReactNode;
  color?: keyof typeof BADGE_COLORS;
}) => (
  <span
    className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${BADGE_COLORS[color]}`}
  >
    {children}
  </span>
);

const SUBSCRIPTION_COLORS: Record<string, keyof typeof BADGE_COLORS> = {
  active: "green",
  cancelled: "orange",
  pending: "yellow",
};

export const SubscriptionBadge = ({ status }: { status?: string | null }) => {
  if (!status) return <Badge color="gray">Sans abonnement</Badge>;
  const labels: Record<string, string> = {
    active: "Abonné",
    cancelled: "Annulé",
    pending: "En attente",
  };
  return (
    <Badge color={SUBSCRIPTION_COLORS[status] ?? "gray"}>
      {labels[status] ?? status}
    </Badge>
  );
};

const BOOK_STATUS_COLORS: Record<string, keyof typeof BADGE_COLORS> = {
  draft: "gray",
  ready_to_generate: "blue",
  ai_generating: "yellow",
  ai_generated: "blue",
  completed: "indigo",
  pdf_generating: "yellow",
  pdf_generated: "green",
  audio_generating: "yellow",
  audio_generated: "purple",
  audio_validated: "green",
};

export const BOOK_STATUS_LABELS: Record<string, string> = {
  draft: "Brouillon",
  ready_to_generate: "Prêt à générer",
  ai_generating: "Écriture IA en cours",
  ai_generated: "Écrit par l'IA",
  completed: "Relu",
  pdf_generating: "PDF en cours",
  pdf_generated: "PDF généré",
  audio_generating: "Audio en cours",
  audio_generated: "Audio généré",
  audio_validated: "Audio validé",
};

export const bookStatusLabel = (status: string) =>
  BOOK_STATUS_LABELS[status] ?? status;

export const BookStatusBadge = ({ status }: { status?: string | null }) => {
  if (!status) return <Badge color="gray">Aucun livre</Badge>;
  return (
    <Badge color={BOOK_STATUS_COLORS[status] ?? "gray"}>
      {bookStatusLabel(status)}
    </Badge>
  );
};

// FIELDS =====================================================================
export const Field = ({
  label,
  value,
  onChange,
  type = "text",
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  type?: string;
}) => (
  <label className="block">
    <span className="text-xs font-medium text-gray-500">{label}</span>
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
    />
  </label>
);

export const NumberField = ({
  label,
  value,
  onChange,
}: {
  label: string;
  value?: number;
  onChange: (value: number) => void;
}) => (
  <label className="block">
    <span className="text-xs font-medium text-gray-500">{label}</span>
    <input
      type="number"
      value={value ?? 0}
      onChange={(e) => onChange(Number(e.target.value))}
      className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
    />
  </label>
);

export const SelectField = ({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: [string, string][];
}) => (
  <label className="block">
    <span className="text-xs font-medium text-gray-500">{label}</span>
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
    >
      {options.map(([val, lbl]) => (
        <option key={val} value={val}>
          {lbl}
        </option>
      ))}
    </select>
  </label>
);

export const PdfLink = ({ url, label }: { url: string; label: string }) => (
  <a
    href={url}
    target="_blank"
    rel="noreferrer"
    className="flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 text-sm font-medium rounded-md hover:bg-gray-50 text-gray-700"
  >
    <FileText className="w-4 h-4" /> {label}
    <Download className="w-3.5 h-3.5" />
  </a>
);

// BUTTONS ====================================================================
const BUTTON_VARIANTS = {
  primary: "bg-indigo-600 text-white hover:bg-indigo-700",
  secondary: "border border-gray-300 text-gray-700 hover:bg-gray-50",
  danger: "border border-red-200 text-red-600 hover:bg-red-50",
  success: "bg-green-600 text-white hover:bg-green-700",
};

export const Button = ({
  children,
  onClick,
  disabled,
  variant = "secondary",
  confirm,
  size = "md",
  type = "button",
}: {
  children: ReactNode;
  onClick?: () => void;
  disabled?: boolean;
  variant?: keyof typeof BUTTON_VARIANTS;
  /** Asks for confirmation before running onClick (destructive actions). */
  confirm?: string;
  size?: "sm" | "md";
  type?: "button" | "submit";
}) => (
  <button
    type={type}
    onClick={() => {
      if (confirm && !window.confirm(confirm)) return;
      onClick?.();
    }}
    disabled={disabled}
    className={`inline-flex items-center gap-1.5 font-medium rounded-md disabled:opacity-50 ${
      size === "sm" ? "px-2.5 py-1 text-xs" : "px-3 py-1.5 text-sm"
    } ${BUTTON_VARIANTS[variant]}`}
  >
    {children}
  </button>
);

export const TextArea = ({
  label,
  value,
  onChange,
  rows = 6,
  mono = false,
}: {
  label?: string;
  value: string;
  onChange: (value: string) => void;
  rows?: number;
  mono?: boolean;
}) => (
  <label className="block">
    {label && (
      <span className="text-xs font-medium text-gray-500">{label}</span>
    )}
    <textarea
      value={value}
      rows={rows}
      onChange={(e) => onChange(e.target.value)}
      className={`mt-1 w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
        mono ? "font-mono text-xs" : ""
      }`}
    />
  </label>
);

export const Loading = () => <div className="text-gray-500">Chargement…</div>;

export const Empty = ({ children }: { children: ReactNode }) => (
  <div className="p-5 text-sm text-gray-400">{children}</div>
);

// PAGINATION =================================================================
/** Footer of every admin list: page navigation and page size (15/25/50/100). */
export const PaginationBar = ({
  pagination,
  onChange,
}: {
  pagination?: Pagination;
  onChange: (params: Required<PageParams>) => void;
}) => {
  if (!pagination) return null;
  const { page, per_page, total, total_pages } = pagination;
  const first = total === 0 ? 0 : (page - 1) * per_page + 1;
  const last = Math.min(page * per_page, total);

  return (
    <div className="flex flex-wrap items-center justify-between gap-3 px-5 py-3 border-t border-gray-100 text-sm text-gray-500">
      <span>
        {first}–{last} sur {total}
      </span>
      <div className="flex items-center gap-3">
        <label className="flex items-center gap-2">
          Par page
          <select
            value={per_page}
            onChange={(e) =>
              onChange({ page: 1, per_page: Number(e.target.value) })
            }
            className="border border-gray-300 rounded-md px-2 py-1 text-sm"
          >
            {PER_PAGE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
        <Button
          size="sm"
          onClick={() => onChange({ page: page - 1, per_page })}
          disabled={page <= 1}
        >
          Précédent
        </Button>
        <span>
          {page} / {total_pages}
        </span>
        <Button
          size="sm"
          onClick={() => onChange({ page: page + 1, per_page })}
          disabled={page >= total_pages}
        >
          Suivant
        </Button>
      </div>
    </div>
  );
};

export const ShopifyOrderWarning = () => (
  <div className="flex gap-2 rounded-md bg-yellow-50 border border-yellow-200 px-3 py-2 text-xs text-yellow-800">
    <AlertTriangle className="w-4 h-4 shrink-0" />
    <p>
      Sans commande Shopify, on perd le lien avec l'achat (paiement, acheteur,
      cadeau). Laissez ce champ vide uniquement pour un utilisateur interne ou
      une invitation spécifique ; pour un client, collez le lien de sa commande
      depuis l'admin Shopify.
    </p>
  </div>
);
